<!--
  SupportTicketForm.svelte — the fields of a new ticket.

  Shared by the customer's "New ticket" page and the agent's "create on behalf"
  page, which differ only in who the requester is: an agent names them by email
  (they must already hold an account), a customer is the requester. Everything
  else — product, category, priority, the report itself and its attachments — is
  the same form, so the two cannot drift into asking different questions.

  Categories are per product (its own plus the global ones), so they are
  reloaded when the product changes — from the change handler, not an effect,
  since the list is a consequence of one user action rather than something to
  keep in sync.
-->
<script lang="ts">
	import { onMount } from 'svelte';
	import { Button, Input, Label, Select, Spinner, Textarea } from 'flowbite-svelte';
	import FormField from './FormField.svelte';
	import RoutedButton from './RoutedButton.svelte';
	import SupportAttachmentPicker from './SupportAttachmentPicker.svelte';
	import SupportUploadProgress from './SupportUploadProgress.svelte';
	import { isValidEmail, listSupportCategories } from '../AppNetwork';
	import type { SupportCategory, SupportTicketInput, SupportTicketPriority } from '../AppTypes';
	import {
		SUPPORT_PRIORITIES,
		guessOperatingSystem,
		supportPriorityLabel
	} from '../support/supportFormat';
	import { t } from '../i18n';

	interface Props {
		/** The products a ticket may be filed against. */
		products: { applicationUUID: string; name: string; ticketPrefix?: string }[];
		/** The product to start on, when the route names one. */
		initialAppUUID?: string;
		/** The closed ticket this one follows up on. */
		followUpOfKey?: string;
		/** Whether an agent is filing on someone's behalf — adds the requester field. */
		onBehalf?: boolean;
		/** Whether a submit is in flight. */
		submitting?: boolean;
		/** Fraction of the attachments uploaded so far, 0–1, while a submit with files is in flight; `null` otherwise. */
		uploadProgress?: number | null;
		/** Where Cancel leads. */
		cancelHref: string;
		/** Receives the ticket. `requesterEmail` is only present on behalf. */
		onsubmit: (_input: SupportTicketInput & { requesterEmail?: string }, _files: File[]) => void;
	}

	let {
		products,
		initialAppUUID = '',
		followUpOfKey = '',
		onBehalf = false,
		submitting = false,
		uploadProgress = null,
		cancelHref,
		onsubmit
	}: Props = $props();

	/** The requester's email address (agents only). */
	let requesterEmail = $state('');
	/** The product selected. */
	let applicationUUID = $state('');
	/** Categories offered for that product. */
	let categories: SupportCategory[] = $state([]);
	/** Whether the category list is loading. */
	let loadingCategories = $state(false);
	/** The category selected; empty is none. */
	let categoryUUID = $state('');
	/** The urgency selected. */
	let priority: SupportTicketPriority = $state('normal');
	/** One-line summary. */
	let subject = $state('');
	/** The report. */
	let body = $state('');
	/** Product version in use. */
	let productVersion = $state('');
	/** Operating system, prefilled from the browser as a best guess. */
	let operatingSystem = $state(guessOperatingSystem(navigator.userAgent));
	/** Files to attach. */
	let files: File[] = $state([]);
	/** Whether the user has tried to submit, which is when missing fields start to show as errors. */
	let attempted = $state(false);

	/** What each priority is for, so "urgent" means the same thing to everyone who picks it. */
	const PRIORITY_HINTS: Record<SupportTicketPriority, string> = {
		low: 'A question or a minor inconvenience.',
		normal: 'Something is not working as expected.',
		high: 'Your work is significantly impaired.',
		urgent: 'Production is down and there is no workaround.'
	};

	/** Products as picker items. */
	let productItems = $derived(
		products.map((product) => ({ value: product.applicationUUID, name: product.name }))
	);

	/** Categories as picker items, with "none" first. */
	let categoryItems = $derived([
		{ value: '', name: $t('No category') },
		...categories.map((category) => ({ value: category.categoryUUID, name: category.name }))
	]);

	/** Priorities as picker items. */
	let priorityItems = $derived(
		SUPPORT_PRIORITIES.map((value) => ({ value, name: $t(supportPriorityLabel(value)) }))
	);

	/** The chosen category, for its description. */
	let selectedCategory = $derived(categories.find((category) => category.categoryUUID === categoryUUID) ?? null);

	/** Per-field validation messages; empty when the field is fine. */
	let errors = $derived({
		requesterEmail:
			onBehalf && !isValidEmail(requesterEmail.trim()) ? $t('Enter the requester’s email address.') : '',
		applicationUUID: applicationUUID ? '' : $t('Choose a product.'),
		subject: subject.trim() ? '' : $t('Give the ticket a subject.'),
		body: body.trim() ? '' : $t('Describe the problem.')
	});

	/** Whether every required field is filled. */
	let isValid = $derived(Object.values(errors).every((message) => message === ''));

	/** Loads the categories offered for a product, keeping the chosen one only if it is still offered. */
	async function loadCategories(appUUID: string): Promise<void> {
		loadingCategories = true;
		/** A failure only costs the optional category picker, never the form. */
		try {
			categories = appUUID ? await listSupportCategories(appUUID) : [];
		} catch {
			categories = [];
		} finally {
			loadingCategories = false;
		}
		/* Drop a category that belongs to the product the user switched away from. */
		if (!categories.some((category) => category.categoryUUID === categoryUUID)) categoryUUID = '';
	}

	/** Switches product and reloads its categories. */
	function selectProduct(event: Event): void {
		applicationUUID = (event.currentTarget as HTMLSelectElement).value;
		loadCategories(applicationUUID);
	}



	/** Validates, then hands the ticket to the page. Blank optional fields are left out. */
	function submit(event: Event): void {
		event.preventDefault();
		attempted = true;
		/* The errors are now on screen beside each field. */
		if (!isValid || submitting) return;
		onsubmit(
			{
				...(onBehalf ? { requesterEmail: requesterEmail.trim() } : {}),
				applicationUUID,
				subject: subject.trim(),
				body: body.trim(),
				priority,
				categoryUUID: categoryUUID || undefined,
				productVersion: productVersion.trim() || undefined,
				operatingSystem: operatingSystem.trim() || undefined,
				followUpOfKey: followUpOfKey || undefined
			},
			files
		);
	}

	onMount(() => {
		/* The route's product when it is one of the choices, else the only choice there is. */
		const initial = products.find((product) => product.applicationUUID === initialAppUUID);
		applicationUUID = initial?.applicationUUID ?? (products.length === 1 ? products[0].applicationUUID : '');
		if (applicationUUID) loadCategories(applicationUUID);
	});
</script>

<form class="space-y-1" onsubmit={submit} novalidate>
	{#if followUpOfKey}
		<p class="text-default mb-4 text-sm">
			<i class="fa-solid fa-link me-1"></i>{$t('Follows up on {{key}}', { key: followUpOfKey })}
		</p>
	{/if}

	{#if onBehalf}
		<FormField
			label={$t('Requester email')}
			id="support-requester"
			required
			description={$t('The customer must already have an account.')}
			error={attempted ? errors.requesterEmail : ''}
		>
			<Input id="support-requester" type="email" bind:value={requesterEmail} placeholder="customer@example.com" />
		</FormField>
	{/if}

	<div class="grid gap-x-4 md:grid-cols-3">
		<FormField label={$t('Product')} id="support-product" required error={attempted ? errors.applicationUUID : ''}>
			<Select
				id="support-product"
				placeholder={$t('Choose a product')}
				items={productItems}
				value={applicationUUID}
				onchange={selectProduct}
			/>
		</FormField>

		<FormField label={$t('Category')} id="support-category" description={selectedCategory?.description ?? ''}>
			{#if loadingCategories}
				<div class="flex h-10 items-center"><Spinner size="5" /></div>
			{:else}
				<Select
					id="support-category"
					placeholder=""
					items={categoryItems}
					value={categoryUUID}
					disabled={!applicationUUID}
					onchange={(event: Event) => (categoryUUID = (event.currentTarget as HTMLSelectElement).value)}
				/>
			{/if}
		</FormField>

		<FormField label={$t('Priority')} id="support-priority" description={$t(PRIORITY_HINTS[priority])}>
			<Select
				id="support-priority"
				placeholder=""
				items={priorityItems}
				value={priority}
				onchange={(event: Event) =>
					(priority = (event.currentTarget as HTMLSelectElement).value as SupportTicketPriority)}
			/>
		</FormField>
	</div>

	<FormField label={$t('Subject')} id="support-subject" required error={attempted ? errors.subject : ''}>
		<Input id="support-subject" bind:value={subject} maxlength={200} placeholder={$t('A short summary of the problem')} />
	</FormField>

	<FormField
		label={$t('Description')}
		id="support-body"
		required
		error={attempted ? errors.body : ''}
		description={$t('What did you do, what did you expect, and what happened instead? Markdown is supported.')}
	>
		<Textarea id="support-body" rows={8} divClass="!w-full" class="!w-full" bind:value={body} />
	</FormField>

	<div class="grid gap-x-4 md:grid-cols-2">
		<FormField label={$t('Product version')} id="support-version">
			<Input id="support-version" bind:value={productVersion} placeholder="2026.1" />
		</FormField>
		<FormField label={$t('Operating system')} id="support-os">
			<Input id="support-os" bind:value={operatingSystem} placeholder="Windows 11" />
		</FormField>
	</div>

	<div class="mb-4">
		<Label for="support-files" class="mb-2">{$t('Attachments')}</Label>
		<SupportAttachmentPicker bind:files id="support-files" />
		<SupportUploadProgress progress={uploadProgress} />
	</div>

	<div class="flex justify-end gap-2 pt-2">
		<RoutedButton color="alternative" href={cancelHref}>{$t('Cancel')}</RoutedButton>
		<Button type="submit" disabled={submitting}>
			{#if submitting}<Spinner size="4" class="me-2" />{:else}<i class="fa-solid fa-paper-plane me-2"></i>{/if}
			{onBehalf ? $t('Create ticket') : $t('Submit ticket')}
		</Button>
	</div>
</form>
